import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import { BlocManagementBanners } from '../../../bloc/BlocManagementBanners';
import { Banner, BannerViewState, CreateBannerDto, UpdateBannerDto } from '../../../../types/banner.types';
import { BannerDialog } from '../../../components/banner/BannerDialog';
import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';

export const BannersManagementPage: React.FC = () => {
    // Bloc instance
    const [bloc] = useState(() => new BlocManagementBanners());

    // UI state
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [dialogVisible, setDialogVisible] = useState<boolean>(false);
    const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Refs
    const toast = useRef<Toast>(null);
    const dt = useRef<DataTable<Banner[]>>(null);

    // Load data on component mount
    useEffect(() => {
        bloc.initData();

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // Toast messages
    const showToast = (severity: 'success' | 'error', summary: string, detail: string) => {
        toast.current?.show({
            severity,
            summary,
            detail,
            life: 3000
        });
    };

    // Handle banner operations
    const handleCreateBanner = () => {
        setSelectedBanner(null);
        setDialogVisible(true);
    };

    const handleEditBanner = (banner: Banner) => {
        setSelectedBanner(banner);
        setDialogVisible(true);
    };

    const handleDeleteBanner = (banner: Banner) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa banner này?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setIsLoading(true);
                    await bloc.deleteBanner(banner.id);
                    showToast('success', 'Thành công', 'Xóa banner thành công');
                } catch (error) {
                    showToast('error', 'Lỗi', 'Không thể xóa banner');
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleSaveBanner = async (data: CreateBannerDto | UpdateBannerDto) => {
        try {
            setIsLoading(true);

            if (selectedBanner) {
                await bloc.updateBanner(selectedBanner.id, data as UpdateBannerDto);
                showToast('success', 'Thành công', 'Cập nhật banner thành công');
            } else {
                await bloc.createBanner(data as CreateBannerDto);
                showToast('success', 'Thành công', 'Thêm mới banner thành công');
            }

            setDialogVisible(false);
        } catch (error) {
            showToast('error', 'Lỗi', 'Không thể lưu banner');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle refresh
    const handleRefresh = () => {
        bloc.refresh();
    };

    // Handle pagination
    const onPageChange = async (event: any) => {
        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            await bloc.changePage(event.page + 1);
        }
    };

    // Table templates
    const indexTemplate = (banner: Banner) => {
        return <span>{banner.index || '-'}</span>;
    };

    const imageTemplate = (banner: Banner) => {
        return (
            <img
                src={banner.url_image}
                alt="Banner"
                style={{ height: '50px', maxWidth: '100px', objectFit: 'contain' }}
                onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/100x50?text=Error')}
            />
        );
    };

    const linkTemplate = (banner: Banner) => {
        return (
            <a
                href={banner.url_banner}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-700"
                title={banner.url_banner}
            >
                {banner.url_banner.length > 30
                    ? banner.url_banner.substring(0, 30) + '...'
                    : banner.url_banner}
            </a>
        );
    };

    const dateTemplate = (banner: Banner) => {
        return (
            <div className="text-sm">
                <div>Tạo: {new Date(banner.created_at).toLocaleString('vi-VN')}</div>
                <div>Cập nhật: {new Date(banner.updated_at).toLocaleString('vi-VN')}</div>
            </div>
        );
    };

    const actionTemplate = (banner: Banner) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-text"
                    onClick={() => handleEditBanner(banner)}
                    tooltip="Chỉnh sửa"
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-text"
                    onClick={() => handleDeleteBanner(banner)}
                    tooltip="Xóa"
                />
            </div>
        );
    };

    // Header template with search and add button
    const renderHeader = () => {
        return (
            <div className="flex flex-wrap justify-content-between align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Tìm kiếm..."
                    />
                </span>
                <div>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-outlined mr-2"
                        onClick={handleRefresh}
                        tooltip="Làm mới"
                    />
                    <Button
                        icon="pi pi-plus"
                        className="p-button"
                        label="Thêm mới"
                        onClick={handleCreateBanner}
                    />
                </div>
            </div>
        );
    };

    return (
        <AdminProtectedRoute
            requiredModule="news-banners-management"
            requiredSubModule="banners-management"
            requiredPermission="view"
        >
            <AdminLayout>
                <div className="card">
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    <StreamBuilder
                        stream={bloc.getStream('info')}
                        initialData={{
                            banners: [],
                            pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
                            loading: true,
                            error: null,
                            filters: {},
                            hasData: false
                        }}
                        builder={(snapshot: any) => {
                            const state: BannerViewState = snapshot.data;

                            return (
                                <div>
                                    <DataTable
                                        ref={dt}
                                        value={state.banners}
                                        scrollHeight='calc(100vh - 200px)'
                                        paginator
                                        rows={state.pagination.limit}
                                        totalRecords={state.pagination.total}
                                        lazy
                                        first={(state.pagination.page - 1) * state.pagination.limit}
                                        onPage={onPageChange}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                        currentPageReportTemplate="{first} - {last} của {totalRecords} banner"
                                        loading={state.loading}
                                        header={renderHeader()}
                                        globalFilter={globalFilter}
                                        emptyMessage="Không tìm thấy banner nào"
                                        showGridlines

                                        dataKey="id"
                                        stripedRows
                                    >
                                        <Column field="index" header="Thứ tự" body={indexTemplate} style={{ minWidth: '80px', textAlign: 'center' }} />
                                        <Column field="url_image" header="Hình ảnh" body={imageTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />
                                        <Column field="url_banner" header="Liên kết" body={linkTemplate} style={{ minWidth: '200px', textAlign: 'center' }} />
                                        <Column field="created_at" header="Thời gian" body={dateTemplate} style={{ minWidth: '200px', textAlign: 'center' }} />
                                        <Column field="action" header="Thao tác" body={actionTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />
                                    </DataTable>

                                    <BannerDialog
                                        visible={dialogVisible}
                                        banner={selectedBanner}
                                        onHide={() => setDialogVisible(false)}
                                        onSave={handleSaveBanner}
                                        isLoading={isLoading}
                                        title={selectedBanner ? 'Chỉnh sửa Banner' : 'Thêm mới Banner'}
                                    />
                                </div>
                            );
                        }}
                    />
                </div>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};
